/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */
import { jsx } from '@amazon/vinyl-tsx'
import { el, hide, show } from '@amazon/vinyl-tsx'
import { isSilentError } from '@amazon/vinyl-util'
import { asData, isObservableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'

// Can be approximate, will remove and GC the child after the transition
const FADE_OUT_DURATION = 1

/**
 * Show a transient toast message.
 *
 * Accepts a string, an HTMLElement, or an `ObservableValue<string>` —
 * passing an observable lets callers hand a `getString(...)` directly
 * (which returns an observable that resolves once the i18n bundle
 * loads), so toasts read from localized strings without each call
 * site having to peek `.value` themselves. The toast snapshots the
 * observable's current value at show time and continues to update if
 * the bundle resolves later.
 */
export function toast(message: string | HTMLElement | ObservableValue<string>, time = 3) {
    // Element inputs (e.g. a custom-composed toast body) mount as-is;
    // strings and observables render inside a shared `.message` wrapper.
    // asData wraps a plain string; observables pass through untouched
    // so vinyl-tsx re-renders when the i18n bundle resolves.
    const div =
        message instanceof HTMLElement ? message : <div className="message">{asData(message)}</div>
    showToast(div, time)
}

// A failing operation often retries (or cascades into siblings) and would
// otherwise stack identical red toasts on top of each other. Suppress the
// duplicate within this window — a fresh occurrence after the window has
// elapsed is treated as a new incident worth surfacing.
const ERROR_DEDUPE_WINDOW_MS = 30_000
const recentErrors = new Map<string, number>()

function messageToObservable(
    message: string | Error | ObservableValue<string> | unknown
): ObservableValue<string> {
    if (typeof message === 'string') return asData(message)
    if (message instanceof Error) return asData(message.message)
    // asData recognizes observables and returns them as-is; everything
    // else falls back to a stringified snapshot.
    return isObservableValue(message) ? message : asData(String(message))
}

export function toastError(message: string | Error | ObservableValue<string> | unknown, time = 5) {
    // Silent errors (e.g. AbortError thrown by abort/withAbort, or
    // any error tagged via createSilentError) signal "we don't want
    // user-facing chrome for this". Honor that contract here so
    // callers don't each have to filter manually.
    if (isSilentError(message)) return
    // Normalize to an observable so the rendered text auto-updates
    // when a passed `getString(...)` bundle resolves.
    const observable = messageToObservable(message)
    const text = observable.value
    const now = performance.now()
    const last = recentErrors.get(text)
    if (last != null && now - last < ERROR_DEDUPE_WINDOW_MS) return
    recentErrors.set(text, now)
    // Opportunistic eviction so the map can't grow without bound when many
    // distinct error strings flow through. Cheap because `now` is monotonic
    // and the entries we care about are recent.
    for (const [key, ts] of recentErrors) {
        if (now - ts >= ERROR_DEDUPE_WINDOW_MS) recentErrors.delete(key)
    }

    const div = (
        <div className="message error" role="alert">
            <Icon icon="exclamationFilled" className="errorIcon" />
            <span className="errorText">{observable}</span>
        </div>
    )
    showToast(div, time)
}

function showToast(div: HTMLElement, time: number) {
    const toastContainer = el('#toastContainer')
    const messages = el('#toastContainer > .messages')
    messages.appendChild(div)
    show(toastContainer)

    setTimeout(() => {
        div.classList.add('hide')
        setTimeout(() => {
            messages.removeChild(div)
            if (messages.children.length === 0) hide(toastContainer)
        }, FADE_OUT_DURATION * 1000)
    }, time * 1000)
}
